"use client";

import { useCallback, useEffect, useState } from "react";
import type { ListingTemplateRow } from "@/lib/listingTemplates";
import {
  deleteListingTemplate,
  duplicateListingTemplate,
  listListingTemplates,
  updateListingTemplate,
} from "@/lib/listingTemplateClient";
import { templateApiUserMessage } from "@/lib/listingTemplateErrors";
import { useAuth } from "@/providers/AuthProvider";
import { listingFormStyles as s } from "./listingFormStyles";
import { theme } from "@/lib/uiTheme";

type Props = {
  templates: ListingTemplateRow[];
  disabled?: boolean;
  hasPropertyDetails: boolean;
  currentDealType: string;
  onApply: (template: ListingTemplateRow) => void;
  onSave: (
    name: string,
    description: string,
  ) => Promise<ListingTemplateRow | null>;
  onStartFromScratch: () => void;
  onTemplatesChange: (templates: ListingTemplateRow[]) => void;
};

export default function TemplateControls({
  templates,
  disabled,
  hasPropertyDetails,
  currentDealType,
  onApply,
  onSave,
  onStartFromScratch,
  onTemplatesChange,
}: Props) {
  const { session, loading: authLoading } = useAuth();
  const [mode, setMode] = useState<"idle" | "save" | "manage">("idle");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const loadTemplates = useCallback(async () => {
    setMessage(null);
    setLoadingTemplates(true);
    try {
      const rows = await listListingTemplates();
      onTemplatesChange(rows);
    } catch (error) {
      setMessage(templateApiUserMessage(error, "load"));
    } finally {
      setLoadingTemplates(false);
    }
  }, [onTemplatesChange]);

  useEffect(() => {
    if (authLoading) return;
    if (!session?.access_token) {
      setMessage("Your session could not be verified. Please sign in again.");
      onTemplatesChange([]);
      return;
    }
    void loadTemplates();
  }, [authLoading, session?.access_token, loadTemplates, onTemplatesChange]);

  function apply(template: ListingTemplateRow) {
    const dealTypeChanges =
      Boolean(template.template_data.deal_type) &&
      template.template_data.deal_type !== currentDealType;
    const needsConfirmation = hasPropertyDetails || dealTypeChanges;
    const messageParts = [
      hasPropertyDetails
        ? "Your address or photos will remain, but other saved form details may change."
        : "",
      dealTypeChanges
        ? "This template changes the deal type, so financing terms will change."
        : "",
    ].filter(Boolean);
    if (
      needsConfirmation &&
      !window.confirm(`Apply "${template.name}"? ${messageParts.join(" ")}`)
    )
      return;
    onApply(template);
    setMode("idle");
  }

  async function save() {
    setMessage(null);
    try {
      const saved = await onSave(name, description);
      if (!saved) {
        setMessage("Template changes could not be saved. Please try again.");
        return;
      }
      onTemplatesChange([
        saved,
        ...templates.filter((template) => template.id !== saved.id),
      ]);
      setName("");
      setDescription("");
      setMode("idle");
    } catch (error) {
      setMessage(templateApiUserMessage(error, "mutate"));
    }
  }

  async function duplicate(template: ListingTemplateRow) {
    setMessage(null);
    try {
      const created = await duplicateListingTemplate(template.id);
      onTemplatesChange([
        created,
        ...templates.filter((item) => item.id !== created.id),
      ]);
    } catch (error) {
      setMessage(templateApiUserMessage(error, "mutate"));
    }
  }

  async function remove(template: ListingTemplateRow) {
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    setMessage(null);
    try {
      await deleteListingTemplate(template.id);
      onTemplatesChange(templates.filter((item) => item.id !== template.id));
    } catch (error) {
      setMessage(templateApiUserMessage(error, "mutate"));
    }
  }

  async function rename(template: ListingTemplateRow) {
    const nextName = window.prompt("Template name", template.name)?.trim();
    if (!nextName || nextName === template.name) return;
    setMessage(null);
    try {
      const updated = await updateListingTemplate(template.id, { name: nextName });
      onTemplatesChange(
        templates.map((item) => (item.id === template.id ? updated : item)),
      );
    } catch (error) {
      setMessage(templateApiUserMessage(error, "mutate"));
    }
  }

  const busy = Boolean(disabled) || authLoading || loadingTemplates;

  return (
    <section style={{ ...s.panel, padding: 16 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <h2 style={{ ...s.sectionTitle, fontSize: 16, marginBottom: 4 }}>
            Listing templates
          </h2>
          <p style={{ ...s.sectionHint, margin: 0 }}>
            Reuse deal structures without copying property-specific details.
          </p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button
            type="button"
            disabled={busy}
            onClick={onStartFromScratch}
            style={s.btnGhost}
          >
            Start from scratch
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setMode("idle")}
            style={s.btnGhost}
          >
            Use template
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setMode("save")}
            style={s.btnGhost}
          >
            Save as template
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setMode("manage")}
            style={s.btnGhost}
          >
            Manage
          </button>
        </div>
      </div>

      {message ? (
        <p style={{ ...s.fieldError, marginBottom: 0 }} role="alert">
          {message}
        </p>
      ) : null}
      {authLoading || loadingTemplates ? (
        <p style={{ ...s.sectionHint, marginTop: 12 }}>Loading templates…</p>
      ) : null}
      {mode === "idle" && !authLoading && !loadingTemplates ? (
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}
        >
          {templates.length ? (
            templates.map((template) => (
              <button
                key={template.id}
                type="button"
                disabled={busy}
                onClick={() => apply(template)}
                style={{ ...s.btnGhost, textAlign: "left" }}
              >
                {template.name}
              </button>
            ))
          ) : (
            <span style={s.sectionHint}>No saved templates yet.</span>
          )}
        </div>
      ) : null}

      {mode === "save" ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(180px, 1fr) minmax(180px, 2fr) auto",
            gap: 8,
            marginTop: 16,
          }}
        >
          <input
            aria-label="Template name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Template name"
            disabled={busy}
            style={s.input}
          />
          <input
            aria-label="Template description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Optional description"
            disabled={busy}
            style={s.input}
          />
          <button
            type="button"
            disabled={busy || !name.trim()}
            onClick={() => void save()}
            style={s.btnPrimary}
          >
            Save
          </button>
        </div>
      ) : null}

      {mode === "manage" ? (
        <div style={{ marginTop: 16 }}>
          {templates.map((template) => (
            <div
              key={template.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
                padding: "10px 0",
                borderBottom: `1px solid ${theme.colors.border}`,
              }}
            >
              <div>
                <strong style={{ color: theme.colors.ink }}>
                  {template.name}
                </strong>
                {template.description ? (
                  <div style={s.sectionHint}>{template.description}</div>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void rename(template)}
                  style={s.btnGhost}
                >
                  Edit
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void duplicate(template)}
                  style={s.btnGhost}
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void remove(template)}
                  style={s.btnGhost}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
